import type { ReactNode } from "react";
import { IconSearch } from "./Icons";

/** Default panoramic banner. Backups: viewer-hero-default-v1.jpg, spielmann-hero-v1.png */
export const DEFAULT_VIEWER_BANNER = "/brand/viewer-hero-default.jpg";
export const DEFAULT_VIEWER_LOGO = "/brand/spielmann-shield.png";

type ViewerPortalHeaderProps = {
  variant: "home" | "compact" | "gate";
  roomName: string;
  subtitle?: string;
  bannerUrl?: string;
  logoUrl?: string;
  brandPrimary: string;
  viewerLabel?: string;
  viewId?: string;
  watermarkEnabled?: boolean;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  showSearch?: boolean;
  children?: ReactNode;
};

export function ViewerPortalHeader({
  variant,
  roomName,
  subtitle,
  bannerUrl,
  logoUrl = DEFAULT_VIEWER_LOGO,
  brandPrimary,
  viewerLabel,
  viewId,
  watermarkEnabled,
  searchQuery = "",
  onSearchChange,
  showSearch = false,
  children,
}: ViewerPortalHeaderProps) {
  const banner = bannerUrl || DEFAULT_VIEWER_BANNER;
  const isGate = variant === "gate";

  return (
    <header
      className={[
        "viewer-portal-header",
        "viewer-portal-header--banner",
        variant === "compact" ? "viewer-portal-header--compact" : "",
        isGate ? "viewer-portal-header--gate" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ "--viewer-brand": brandPrimary } as React.CSSProperties}
    >
      <div
        className="viewer-portal-header__banner"
        style={{ backgroundImage: `url(${banner})` }}
      >
        <div className="viewer-portal-header__banner-scrim" aria-hidden="true" />
        <div className="viewer-portal-header__caption">
          <div className="viewer-portal-header__logo-card">
            <img className="viewer-portal-header__logo" src={logoUrl} alt="" />
          </div>
          <div className="viewer-portal-header__titles">
            <h1 className="viewer-portal-header__title">{roomName}</h1>
            {subtitle ? <p className="viewer-portal-header__subtitle">{subtitle}</p> : null}
          </div>
        </div>
      </div>

      {variant === "home" &&
      ((showSearch && onSearchChange) || viewerLabel || viewId || watermarkEnabled) ? (
        <div className="viewer-portal-header__toolbar">
          {viewerLabel || viewId || watermarkEnabled ? (
            <div className="viewer-portal-header__meta">
              {viewerLabel ? (
                <span className="viewer-portal-header__chip">
                  Viewing as <strong>{viewerLabel}</strong>
                </span>
              ) : null}
              {viewId ? <span className="viewer-portal-header__chip">View ID {viewId}</span> : null}
              {watermarkEnabled ? (
                <span className="viewer-portal-header__chip viewer-portal-header__chip--accent">Watermarked</span>
              ) : null}
            </div>
          ) : (
            <div className="viewer-portal-header__meta" aria-hidden="true" />
          )}
          {showSearch && onSearchChange ? (
            <div className="viewer-portal-header__searchbar">
              <label className="viewer-portal-header__search search-field">
                <IconSearch className="search-field__icon" />
                <input
                  className="input viewer-portal-header__search-input"
                  placeholder="Search this Space"
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  aria-label="Search files in this Space"
                />
              </label>
            </div>
          ) : null}
        </div>
      ) : null}

      {children}
    </header>
  );
}
